<template>
  <div class="fulfillment-orders-card">
    <div
      v-if="loading"
      class="flex items-center justify-center py-8">
      <div class="flex items-center text-gray-500">
        <svg class="animate-spin h-5 w-5 mr-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <span class="text-sm font-medium">Loading fulfillment orders...</span>
      </div>
    </div>

    <div
      v-else
      class="space-y-4">
      <!-- Existing Orders -->
      <div
        v-if="fulfillmentOrders.length > 0"
        class="space-y-3">
          <div
            v-for="order in fulfillmentOrders"
            :key="order.id"
            class="p-4 bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200">
            <div class="flex items-center justify-between">
              <a
                :href="order.linkUrl"
                target="_blank"
                rel="noopener noreferrer"
                class="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium hover:underline">
                <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"></path>
                </svg>
                {{ order.linkTitle }}
              </a>
              <div class="flex items-center space-x-1">
                <button
                  v-if="!order.editing"
                  type="button"
                  class="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                  title="Edit"
                  @click="startEdit(order)">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
                  </svg>
                </button>
                <button
                  v-if="!order.editing"
                  type="button"
                  class="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors duration-200"
                  title="Delete"
                  @click="deleteOrder(order)">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                  </svg>
                </button>
              </div>
            </div>

            <!-- Edit Form -->
            <div
              v-if="order.editing"
              class="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div class="space-y-4">
                <div>
                  <label for="edit-title" class="block mb-2 text-sm font-medium text-gray-900">Link Title</label>
                  <input
                    v-model="order.editTitle"
                    type="text"
                    id="edit-title"
                    class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                    placeholder="e.g., Purchase Order #12345">
                </div>
                <div>
                  <label for="edit-url" class="block mb-2 text-sm font-medium text-gray-900">Link URL</label>
                  <input
                    v-model="order.editUrl"
                    type="url"
                    id="edit-url"
                    class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                    placeholder="https://example.com/order/12345">
                </div>
                <div class="flex gap-2">
                  <button
                    type="button"
                    class="text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                    :disabled="!order.editTitle || !order.editUrl"
                    @click="saveEdit(order)">
                    <svg class="w-4 h-4 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    Save
                  </button>
                  <button
                    type="button"
                    class="text-gray-900 bg-white border border-gray-300 hover:bg-gray-100 focus:ring-4 focus:ring-gray-200 font-medium rounded-lg text-sm px-5 py-2.5 focus:outline-none"
                    @click="cancelEdit(order)">
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

      <!-- No Orders Message -->
      <div
        v-else
        class="text-center py-8">
        <div class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-gray-100">
          <svg class="h-6 w-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"></path>
          </svg>
        </div>
        <p class="mt-2 text-sm text-gray-500">
          No fulfillment orders yet
        </p>
      </div>

      <!-- Add New Order Form -->
      <div class="pt-4 border-t border-gray-200">
        <button
          v-if="!showNewForm"
          type="button"
          class="w-full text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center inline-flex items-center justify-center"
          @click="showNewForm = true">
          <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
          </svg>
          Add Fulfillment Order
        </button>

        <div
          v-else
          class="p-6 bg-blue-50 border border-blue-200 rounded-lg">
          <h4 class="text-lg font-medium text-gray-900 mb-4">
            New Fulfillment Order
          </h4>
          <div class="space-y-4">
            <div>
              <label for="new-title" class="block mb-2 text-sm font-medium text-gray-900">Link Title</label>
              <input
                v-model="newOrder.linkTitle"
                type="text"
                id="new-title"
                class="bg-white border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                placeholder="e.g., Purchase Order #12345">
            </div>
            <div>
              <label for="new-url" class="block mb-2 text-sm font-medium text-gray-900">Link URL</label>
              <input
                v-model="newOrder.linkUrl"
                type="url"
                id="new-url"
                class="bg-white border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                placeholder="https://example.com/order/12345">
            </div>
            <div class="flex gap-2">
              <button
                type="button"
                class="text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 focus:outline-none inline-flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                :disabled="!newOrder.linkTitle || !newOrder.linkUrl || saving"
                @click="createOrder">
                <svg v-if="saving" class="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <svg v-else class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4"></path>
                </svg>
                {{ saving ? 'Saving...' : 'Save' }}
              </button>
              <button
                type="button"
                class="text-gray-900 bg-white border border-gray-300 hover:bg-gray-100 focus:ring-4 focus:ring-gray-200 font-medium rounded-lg text-sm px-5 py-2.5 focus:outline-none"
                :disabled="saving"
                @click="cancelNew">
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import mdtoast from '@dmuy/toast'
import axios from '../init/axios'

const props = defineProps({
  organizationId: {
    type: String,
    required: true
  }
})

// State
const loading = ref(true)
const saving = ref(false)
const fulfillmentOrders = ref([])
const showNewForm = ref(false)
const newOrder = ref({
  linkTitle: '',
  linkUrl: ''
})

// Methods
const fetchOrders = async() => {
  try {
    const response = await axios.get(`/ajax/organizations/${props.organizationId}/fulfillment_orders`)
    if (response.data && response.data.fulfillmentOrders) {
      fulfillmentOrders.value = response.data.fulfillmentOrders.map((order) => ({
        ...order,
        editing: false,
        editTitle: order.linkTitle,
        editUrl: order.linkUrl
      }))
    } else {
      // If response doesn't have expected structure, just set empty array
      fulfillmentOrders.value = []
    }
  } catch (error) {
    // Only show error if it's not a 404 (which would mean no orders yet)
    if (error.response && error.response.status !== 404) {
      // console.error('Error fetching fulfillment orders:', error.response || error)
      mdtoast.error('Failed to load fulfillment orders')
    } else {
      fulfillmentOrders.value = []
    }
  } finally {
    loading.value = false
  }
}

const createOrder = async() => {
  if (!newOrder.value.linkTitle || !newOrder.value.linkUrl) return

  saving.value = true
  try {
    const response = await axios.post(
      `/ajax/organizations/${props.organizationId}/fulfillment_orders`,
      { fulfillmentOrder: newOrder.value }
    )

    if (response.data.success) {
      fulfillmentOrders.value.unshift({
        ...response.data.fulfillmentOrder,
        editing: false,
        editTitle: response.data.fulfillmentOrder.linkTitle,
        editUrl: response.data.fulfillmentOrder.linkUrl
      })
      newOrder.value = { linkTitle: '', linkUrl: '' }
      showNewForm.value = false
      mdtoast.success('Fulfillment order added successfully')
    }
  } catch (error) {
    // console.error('Error creating fulfillment order:', error)
    mdtoast.error('Failed to create fulfillment order')
  } finally {
    saving.value = false
  }
}

const startEdit = (order) => {
  order.editing = true
  order.editTitle = order.linkTitle
  order.editUrl = order.linkUrl
}

const cancelEdit = (order) => {
  order.editing = false
  order.editTitle = order.linkTitle
  order.editUrl = order.linkUrl
}

const saveEdit = async(order) => {
  if (!order.editTitle || !order.editUrl) return

  try {
    const response = await axios.patch(
      `/ajax/organizations/${props.organizationId}/fulfillment_orders/${order.id}`,
      {
        fulfillmentOrder: {
          linkTitle: order.editTitle,
          linkUrl: order.editUrl
        }
      }
    )

    if (response.data.success) {
      order.linkTitle = order.editTitle
      order.linkUrl = order.editUrl
      order.editing = false
      mdtoast.success('Fulfillment order updated')
    }
  } catch (error) {
    // console.error('Error updating fulfillment order:', error)
    mdtoast.error('Failed to update fulfillment order')
  }
}

const deleteOrder = async(order) => {
  if (!window.confirm('Are you sure you want to delete this fulfillment order?')) return

  try {
    const response = await axios.delete(`/ajax/organizations/${props.organizationId}/fulfillment_orders/${order.id}`)

    if (response.data.success) {
      const index = fulfillmentOrders.value.findIndex((o) => o.id === order.id)
      if (index > -1) {
        fulfillmentOrders.value.splice(index, 1)
      }
      mdtoast.success('Fulfillment order deleted')
    }
  } catch (error) {
    // console.error('Error deleting fulfillment order:', error)
    mdtoast.error('Failed to delete fulfillment order')
  }
}

const cancelNew = () => {
  showNewForm.value = false
  newOrder.value = { linkTitle: '', linkUrl: '' }
}

// Lifecycle
onMounted(() => {
  fetchOrders()
})
</script>

<style scoped>
.fulfillment-orders-card {
  @apply p-0;
}
</style>
